import React from 'react';

import { IS_DEVELOPMENT, LANGUAGE, noop, silenceError } from '@nfctron/nexus-core';
import { IntlProvider, UiToaster, UiTooltipProvider } from '@nfctron/nexus-react';
import { TicketsMantineProvider } from '@nfctron/nexus-tickets';

import { Loader, MantineProvider } from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import { ModalsProvider } from '@mantine/modals';
import { type DehydratedState, HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type AppProps } from 'next/app';
import { Poppins } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import appWithI18n from 'next-translate/appWithI18n';

import { mantineThemeConfig } from '@root/styles/mantine.theme';

import { ApiClientProvider } from '@root/features/api-client/api-client.context';
import { AuthProvider } from '@root/features/authentication/services/auth.provider';
import { VisualEffectsProvider } from '@root/features/layout/components/VisualEffects/VisualEffects';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { languageEnumTickets } from '@root/lib/constants/language';
import { useInitTrackingModule } from '@root/lib/hooks/useInitTrackingModule';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { REGISTERED_MODALS } from '@root/lib/modals/constants/register';
import { CookieConsentProvider } from '@root/lib/modules/cookies/CookiesConsentProvider';
import { type PageWithCommonProps } from '@root/lib/modules/next/next.module';
import { TrackingModuleProvider } from '@root/lib/modules/tracking/TrackingProvider';

import '@root/styles/globals.css';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const i18nConfig = require('@root/config/i18n');

/** font-family (Poppins) */
const poppins = Poppins({
	weight: ['400', '500', '600', '700'],
	subsets: ['latin'],
});

/**
 * Base Application wrapper
 */
function AppConfig({ Component, pageProps, ...props }: AppConfigProps) {
	const { t, lang } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const languageId = silenceError(() => languageEnumTickets.get(lang, 'code').languageId, LANGUAGE.CZECH);
	const router = useRouter();
	const [queryClient] = React.useState(() => new QueryClient());
	const { trackingModule, analyticsModule } = useInitTrackingModule({
		fbPixel: pageProps.tracking?.fbPixel ?? [],
		gtagJs: pageProps.tracking?.gtagJs ?? [],
		googleTagManager: pageProps.tracking?.googleTagManager ?? [],
		ttqPixel: pageProps.tracking?.ttqPixel ?? [],
		msClarity: pageProps.tracking?.msClarity ?? [],
		sklik: pageProps.tracking?.sklik ?? [],
	});

	/** hotkey to reload translation resources in development stage */
	useHotkeys([
		[
			'shift+r',
			() => {
				if (!IS_DEVELOPMENT) return;
				console.debug('[next-i18next] Reloading translation resources...');
				router
					.replace(window.location.pathname, undefined, { scroll: false })
					.then(() => console.debug('[next-i18next] Translation resources reloaded'));
			},
		],
	]);

	return (
		<React.Fragment>
			{/* font-family (Poppins) */}
			<style jsx global>{`
				body {
					font-family: ${poppins.style.fontFamily};
					font-weight: ${poppins.style.fontWeight};
					font-style: ${poppins.style.fontStyle};
				}
			`}</style>

			{/* providers */}
			<MantineProvider theme={mantineThemeConfig}>
				<TicketsMantineProvider theme={mantineThemeConfig}>
					<IntlProvider languageId={languageId} setLanguageId={noop}>
						<QueryClientProvider client={queryClient}>
							<HydrationBoundary state={pageProps.dehydratedQueryClientState}>
								<React.Suspense
									fallback={
										<div className="flex h-screen w-screen items-center justify-center bg-zinc-50">
											<Loader />
										</div>
									}
								>
									<ApiClientProvider>
										<TrackingModuleProvider trackingModule={trackingModule} analyticsModule={analyticsModule}>
											<CookieConsentProvider>
												{/* page head*/}
												<Head>
													{/* viewport */}
													<meta name="viewport" content="width=device-width, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no" />

													{/* title */}
													<title>{process.env.NEXT_PUBLIC_APP_NAME}</title>

													{/* Meta product catalog feed, @see https://www.facebook.com/business/help/120325381656392?id=725943027795860 */}
													<link
														key="meta-catalog-feed"
														rel="alternate"
														type="application/rss+xml"
														title="Meta Product Catalog Feed"
														href="/meta-catalog-feed.xml"
													/>

													{/* default iOS deeplink without app-argument, @see https://developer.apple.com/documentation/webkit/promoting_apps_with_smart_app_banners */}
													<meta key="apple-itunes-app" name="apple-itunes-app" content="app-id=1595341751" />

													{/* favicons */}
													{/* must be here since when it is in _document.config.tsx, any later dynamic meta tag changes are not reflected */}
													<meta key="theme-color" name="theme-color" content="#FFFFFF" />
													<link key="favicon" rel="icon" href="/static/favicon.ico" />
													<link key="apple-touch-icon" rel="apple-touch-icon" sizes="180x180" href="/static/apple-touch-icon.png" />
													<link key="favicon-32x32" rel="icon" type="image/png" sizes="32x32" href="/static/favicon-32x32.png" />
													<link key="favicon-16x16" rel="icon" type="image/png" sizes="16x16" href="/static/favicon-16x16.png" />
													<link key="manifest" rel="manifest" href="/static/site.webmanifest" />
													<link key="mask-icon" rel="mask-icon" href="/static/safari-pinned-tab.svg" color="#25196A" />
													<link key="shortcut-icon" rel="shortcut icon" href="/static/favicon.ico" />
													<meta key="apple-mobile-web-app-title" name="apple-mobile-web-app-title" content={process.env.NEXT_PUBLIC_APP_NAME} />
													<meta key="application-name" name="application-name" content={process.env.NEXT_PUBLIC_APP_NAME} />
													<meta key="msapplication-TileColor" name="msapplication-TileColor" content="#25196A" />
													<meta key="msapplication-config" name="msapplication-config" content="/static/browserconfig.xml" />
												</Head>

												<AuthProvider>
													<VisualEffectsProvider>
														<UiTooltipProvider>
															<ModalsProvider
																modals={REGISTERED_MODALS}
																labels={{
																	cancel: t('cancelButton.text'),
																	confirm: t('confirmButton.text'),
																}}
																modalProps={{
																	zIndex: 300,
																}}
															>
																{/* page render */}
																<Component {...pageProps} {...props} />
															</ModalsProvider>

															{/* ui toaster */}
															<UiToaster />
														</UiTooltipProvider>
													</VisualEffectsProvider>
												</AuthProvider>
											</CookieConsentProvider>
										</TrackingModuleProvider>
									</ApiClientProvider>
								</React.Suspense>
							</HydrationBoundary>
						</QueryClientProvider>
					</IntlProvider>
				</TicketsMantineProvider>
			</MantineProvider>
		</React.Fragment>
	);
}

/**
 * Base App page props type
 * @private
 */
type PageProps = PageWithCommonProps<{
	dehydratedQueryClientState?: DehydratedState;
}>;

/**
 * Base App config props type
 * @private
 */
type AppConfigProps = AppProps & {
	pageProps: PageProps;
};

/**
 * App default export
 * @default
 */
export default appWithI18n(AppConfig as React.FunctionComponent, { ...i18nConfig, skipInitialProps: true });
